import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SRS_RATING_HINTS,
  SRS_RATING_LABELS,
  SRS_RATINGS,
  highlightWord,
  hintLevelFor,
  maskWord,
  type ReviewCardView,
  type SrsRating,
} from '@en3/shared';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardBody } from '@/components/ui/card';

/**
 * 复习卡（单张）。
 *
 * 卡面信息的分寸感是这张卡的全部设计：
 * - **正面绝不显示释义**。显示释义的复习卡只是在"读一遍"，不产生回想（retrieval），
 *   而回想才是记忆变牢的原因。因此正面只有：词形、音标、按熟悉度给的提示、挖空句。
 * - **提示按熟悉度分档**（`hintLevelFor`）：不熟的给"首字母 + 长度"的拼写骨架，
 *   半熟的只给词性，熟的一律不给 —— 给多了会变成"认出来"，而不是"想起来"。
 * - **翻面前不能评分**：按钮 disabled 并说明原因。否则用户会习惯性地点"记得"，
 *   数据就全是噪声，调度也就跟着失效（这是间隔重复最常见的失败模式）。
 *
 * 键盘：空格翻面、0–4 评分、Esc 退出（退出会把已评分的部分提交，见 ReviewPage）。
 * 为什么把键盘处理放在这张卡里：它依赖 `flipped`（未翻面时数字键必须无效）与
 * "每张卡只评一次"的时序，放在父组件里会多一层状态同步。
 *
 * 每次换卡由父组件用 `key` 强制重挂载，因此这里的 `flipped` 天然不会串到下一张
 * （比用 effect 监听 record.id 变化去重置更不容易出错）。
 */
export function ReviewFlashcard({
  card,
  onRate,
  onExit,
  disabled = false,
}: {
  card: ReviewCardView;
  onRate: (rating: SrsRating) => void;
  /** Esc：结束本次会话（父组件负责把已评分的部分提交） */
  onExit: () => void;
  /** 提交进行中：禁止继续评分，避免"提交中的评分"落进已经发出去的批次 */
  disabled?: boolean;
}) {
  const [flipped, setFlipped] = useState(false);
  const hintId = useId();
  const record = card.record;
  const phonetic = record.phoneticUk ?? record.phoneticUs;
  const hintLevel = hintLevelFor(record.familiarity);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (disabled) return;
      // 带修饰键的是浏览器/输入法快捷键（Ctrl+1 切换标签页等），不要抢
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.repeat) return; // 长按不算连评

      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault(); // 同时阻止页面滚动与聚焦按钮被"空格"再点一次
        setFlipped(true);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        onExit();
        return;
      }
      if (!flipped) return; // 未翻面时数字键无效：评分必须发生在回想之后
      if (/^[0-4]$/.test(event.key)) {
        event.preventDefault();
        onRate(Number(event.key) as SrsRating);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disabled, flipped, onExit, onRate]);

  return (
    <div className="mt-3">
      <Card>
        <CardBody>
          {flipped ? (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-2xl font-semibold break-all">{record.surface}</p>
                <div className="mt-1.5 flex flex-wrap items-center justify-center gap-2">
                  {phonetic && <span className="font-mono text-xs text-muted-foreground">{phonetic}</span>}
                  {record.pos && <Badge tone="muted">{record.pos}</Badge>}
                  {record.cefrLevel && <Badge tone="primary">{record.cefrLevel}</Badge>}
                </div>
                {record.firstArticleId && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    首次遇见：
                    <Link to={ROUTES.reader(record.firstArticleId)} className="text-primary underline underline-offset-4">
                      {record.firstArticleTitle ?? '查看原文'}
                    </Link>
                  </p>
                )}
              </div>

              <section>
                <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">释义</h3>
                <p className="mt-1 text-base">{record.translation ?? '暂无释义（这个词还没被词典收录，可在生词本里自己补一条笔记）'}</p>
              </section>

              {card.context && (
                <section>
                  <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">它出现的那句话</h3>
                  <p className="mt-1 rounded-md bg-muted/50 px-3 py-2 font-serif leading-relaxed">
                    <ContextWithHighlight context={card.context} surface={record.surface} />
                  </p>
                </section>
              )}

              {record.note && (
                <section>
                  <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">我的笔记</h3>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{record.note}</p>
                </section>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setFlipped(true)}
              // 按钮有明确的无障碍名（读屏只念这一句），卡片上的提示与挖空句通过
              // aria-describedby 关联过去 —— 否则 aria-label 会把它们整个盖掉。
              aria-label={`翻面查看「${record.surface}」的释义（快捷键：空格）`}
              aria-describedby={hintId}
              title="翻面（空格）"
              className="w-full rounded-md px-2 py-6 text-center hover:bg-accent/40"
            >
              <span className="block text-3xl font-semibold break-all">{record.surface}</span>
              {phonetic && <span className="mt-1 block font-mono text-sm text-muted-foreground">{phonetic}</span>}

              <span id={hintId} className="mt-3 block space-y-2 text-xs text-muted-foreground">
                {hintLevel === 'initial' && (
                  <span className="block">
                    拼写提示：<code className="font-mono tracking-widest">{maskWord(record.surface)}</code>
                  </span>
                )}
                {/* 半熟只给词性；没有词性就干脆不给提示，宁缺毋滥 */}
                {hintLevel === 'pos' && record.pos && (
                  <span className="block">
                    词性提示：<code className="font-mono">{record.pos}</code>
                  </span>
                )}
                {card.clozeText && <ClozeLine text={card.clozeText} />}
                <span className="block">先在心里说出它的意思，再点卡片（或按空格）翻面</span>
              </span>
            </button>
          )}
        </CardBody>
      </Card>

      <div className="mt-4 space-y-2">
        {!flipped && (
          <p role="status" className="text-center text-xs text-muted-foreground">
            先回想，再翻面 —— 翻面后才能自评。
          </p>
        )}
        <div className="grid grid-cols-5 gap-1.5">
          {SRS_RATINGS.map((rating) => (
            <Button
              key={rating}
              variant="outline"
              size="sm"
              disabled={!flipped || disabled}
              onClick={() => onRate(rating)}
              title={`${SRS_RATING_LABELS[rating]}：${SRS_RATING_HINTS[rating]}（快捷键 ${rating}）`}
              aria-label={`评分 ${rating}：${SRS_RATING_LABELS[rating]}（快捷键 ${rating}）`}
              className="h-auto flex-col gap-0.5 px-1 py-2"
            >
              <span aria-hidden className="font-mono text-[10px] text-muted-foreground">
                {rating}
              </span>
              <span className="text-[11px] leading-tight">{SRS_RATING_LABELS[rating]}</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * 挖空句：把 `_` 串用等宽字体渲染，空长度才能一眼看出"这里缺几个字母"。
 * 用 `split` 保留分隔符而不是 `replace` 成 HTML 片段，是为了不引入 dangerouslySetInnerHTML。
 */
function ClozeLine({ text }: { text: string }) {
  const parts = text.split(/(_+)/);
  return (
    <span className="block font-serif text-sm leading-relaxed">
      {parts.map((part, index) =>
        part.startsWith('_') ? (
          <span key={`${part}-${index}`} className="mx-0.5 font-mono tracking-widest">
            {part}
          </span>
        ) : (
          <span key={`${part}-${index}`}>{part}</span>
        ),
      )}
    </span>
  );
}

/**
 * 原句里把目标词标出来。
 *
 * 用 `highlightWord`（共享库，按词形归并匹配）而不是 `indexOf`：用户点的是 `studies`，
 * 生词本里存的是 `study`，直接字符串匹配会标不到任何东西。
 * 返回三段分别渲染，不做字符串替换成 HTML —— 少一个 XSS 面。
 */
function ContextWithHighlight({ context, surface }: { context: string; surface: string }) {
  const parts = highlightWord(context, surface);
  if (!parts) return <>{context}</>;
  return (
    <>
      {parts.before}
      <mark className="rounded bg-primary/15 px-0.5 font-semibold text-primary">{parts.match}</mark>
      {parts.after}
    </>
  );
}
